import { type YurplanConfigurationDto } from '@/pages/yurplanTickets/yurplanTickets.types'
import { ErrorService } from '@/shared/service/errorService'
import { type Attendee } from '@/shared/types/attendee.type'
import { createStore, type GettersObj } from '@/utils/createStore'

import { TicketsService } from './yurplanTickets.service'

export type GettersTickets = GettersObj<typeof mapGettersTickets>
export class YurplanTicketsState {
  yurplanCfg: YurplanConfigurationDto = {
    clientId: '',
    clientSecret: '',
    username: '',
    password: '',
    eventId: '',
  }
  attendees: Attendee[] = []
  pagination = {
    sortBy: ['lastName'],
    sortDesc: [false],
    itemsPerPage: 15,
    page: 1,
    totalItems: 0,
  }
  filter = {
    search: '',
  }
  loading = 0
}

export const {
  yurplanTickets,
  dispatch: dispatchTickets,
  mapGetters: mapGettersTickets,
  mapState: mapStateTickets,
} = createStore({
  moduleName: 'yurplanTickets',
  namespaced: true,
  initState: new YurplanTicketsState(),
  mutations: {
    LOAD(state) {
      state.loading = state.loading + 1
    },
    SET_YURPLAN_CFG(state, yurplanCfg: YurplanConfigurationDto) {
      state.yurplanCfg = yurplanCfg
      state.loading = state.loading - 1
    },
    SET_ATTENDEES(
      state,
      { attendees, total }: { attendees: Attendee[]; total: number },
    ) {
      state.attendees = attendees
      state.pagination.totalItems = total
      state.loading = state.loading - 1
    },
    SET_PAGINATION(state, pagination: YurplanTicketsState['pagination']) {
      state.pagination = pagination
    },
    SET_FILTER(state, filter: YurplanTicketsState['filter']) {
      state.filter = filter
    },
    SET_VIEW(state) {
      state.loading = state.loading - 1
    },
  },
  getters: {},
  actions: {
    async loadYurplanCfg({ commit }) {
      commit('LOAD')
      try {
        const yurplanCfg = await TicketsService.getYurplanConfiguration()
        commit('SET_YURPLAN_CFG', yurplanCfg)
      } catch (e) {
        ErrorService.handleError(e)
        commit('SET_VIEW')
      }
    },

    async updateYurplanCfg(
      { commit },
      {
        yurplanCfg,
      }: {
        yurplanCfg: YurplanConfigurationDto
      },
    ) {
      commit('LOAD')
      try {
        const updatedCfg =
          await TicketsService.updateYurplanConfiguration(yurplanCfg)
        commit('SET_YURPLAN_CFG', updatedCfg)
      } catch (e) {
        ErrorService.handleError(e)
        commit('SET_VIEW')
      }
    },

    async list(
      { commit, state },
      payload: {
        filter?: YurplanTicketsState['filter']
        pagination?: YurplanTicketsState['pagination']
      } = {},
    ) {
      commit('LOAD')
      if (payload.pagination) {
        commit('SET_PAGINATION', {
          ...state.pagination,
          ...payload.pagination,
        })
      }
      if (payload.filter) {
        commit('SET_FILTER', { ...state.filter, ...payload.filter })
      }
      const { sortBy, sortDesc, page, itemsPerPage } = state.pagination
      try {
        const { attendees, total } = await TicketsService.getAll(
          state.filter.search,
          page,
          itemsPerPage,
          sortBy[0],
          sortDesc[0],
        )
        commit('SET_ATTENDEES', { attendees, total })
      } catch (e) {
        ErrorService.handleError(e)
        commit('SET_VIEW')
      }
    },

    async syncYurplan({ commit, dispatch }) {
      commit('LOAD')
      try {
        await TicketsService.syncYurplan()
        await dispatch('list')
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('SET_VIEW')
      }
    },

    async importAttendees({ commit, dispatch }, file: File) {
      commit('LOAD')
      try {
        const x64File = await new Promise<string | ArrayBuffer | null>(
          (resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = (): void => {
              resolve(reader.result)
            }
            reader.onerror = (error): void => {
              console.error(error)
              reject(
                error instanceof Error
                  ? error
                  : new Error('Error reading file'),
              )
            }
          },
        )
        await TicketsService.importTickets(x64File)
        await dispatch('list')
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('SET_VIEW')
      }
    },
  },
})
